import { ActionIcon, Anchor, Autocomplete, Button, Grid, Group, Modal, Select, Stack, Switch, Text, TextInput, Tooltip } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { COMMON_DOCUMENTS, DOSAGE_FORMS, PRODUCT_CATEGORIES, type CountryDto } from '@mexp/shared';
import { errorMessage, http } from '@/api/client';
import { useCountries, useExporterLookup } from '@/features/shared/lookups';
import { useBdsReviewers } from '../api';
import { notifyError, notifySuccess } from '@/lib/notify';

export interface ChecklistRow {
  key: string;
  /** Key of an existing checklist item (edits keep it). */
  savedKey?: string;
  name: string;
  required: boolean;
  notes: string;
}

export const emptyChecklistRow = (name = ''): ChecklistRow => ({ key: Math.random().toString(36).slice(2), name, required: true, notes: '' });

type FieldProps = { value?: string | null; onChange?: (v: string | null) => void; error?: ReactNode };

/** Country select limited to the master list, with an inline "add country". */
export function CountrySelect({ value, onChange, error }: FieldProps) {
  const countries = useCountries();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const add = useMutation({
    mutationFn: () => http.post<CountryDto>('/countries', { name: name.trim() }),
    onSuccess: async (c) => {
      await queryClient.invalidateQueries({ queryKey: ['countries'] });
      onChange?.(c.name);
      setAdding(false);
      notifySuccess(`${c.name} added to the country list.`);
    },
    onError: (e) => notifyError(e),
  });
  return (
    <>
      <Select
        label="Country"
        placeholder="Destination country"
        data={(countries.data ?? []).map((c) => c.name)}
        value={value}
        onChange={onChange}
        searchable
        withAsterisk
        error={error}
        limit={50}
        nothingFoundMessage="Not in the list"
        description={
          <Anchor component="button" type="button" fz="xs" onClick={() => setAdding(true)}>
            Country missing? Add it
          </Anchor>
        }
      />
      <Modal opened={adding} onClose={() => setAdding(false)} title="Add a country" size="sm">
        <Stack>
          <TextInput label="Country name" value={name} onChange={(e) => setName(e.currentTarget.value)} data-autofocus />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button onClick={() => add.mutate()} loading={add.isPending} disabled={!name.trim()}>
              Add
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

export function ExporterSelect({ value, onChange, error }: FieldProps) {
  const exporters = useExporterLookup();
  return (
    <Select
      label="Exporter"
      placeholder="Search exporter company"
      data={(exporters.data ?? []).map((e) => ({ value: e.id, label: e.companyName }))}
      value={value}
      onChange={onChange}
      searchable
      withAsterisk
      error={error}
      limit={50}
      nothingFoundMessage="No active exporter with that name"
    />
  );
}

export function CategorySelect(props: FieldProps) {
  return <Select label="Category" placeholder="Optional" data={[...PRODUCT_CATEGORIES]} clearable {...props} />;
}

export function DosageFormSelect(props: FieldProps) {
  return <Select label="Dosage form" placeholder="Optional" data={[...DOSAGE_FORMS]} clearable searchable {...props} />;
}

/** The required-documents checklist of a request. */
export function ChecklistEditor({ rows, onChange }: { rows: ChecklistRow[]; onChange: (rows: ChecklistRow[]) => void }) {
  const update = (key: string, patch: Partial<ChecklistRow>) => onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const used = new Set(rows.map((r) => r.name.trim().toLowerCase()));
  const suggestions = COMMON_DOCUMENTS.filter((d) => !used.has(d.toLowerCase()));
  return (
    <Stack gap="sm">
      {rows.length === 0 && (
        <Text fz="sm" c="dimmed">
          List the documents this dossier needs. Tasks can then be linked to each item.
        </Text>
      )}
      {rows.map((row, i) => (
        <Grid key={row.key} gutter="xs" align="flex-end">
          <Grid.Col span={{ base: 12, sm: 5 }}>
            <Autocomplete
              label={i === 0 ? 'Document' : undefined}
              placeholder="e.g. Finished Product COA"
              data={suggestions}
              value={row.name}
              onChange={(name) => update(row.key, { name })}
              limit={10}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 5 }}>
            <TextInput label={i === 0 ? 'Notes' : undefined} placeholder="Optional" value={row.notes} onChange={(e) => update(row.key, { notes: e.currentTarget.value })} />
          </Grid.Col>
          <Grid.Col span={{ base: 9, sm: 1.5 }}>
            <Switch label="Required" checked={row.required} onChange={(e) => update(row.key, { required: e.currentTarget.checked })} mb={8} />
          </Grid.Col>
          <Grid.Col span={{ base: 3, sm: 0.5 }}>
            <Tooltip label="Remove">
              <ActionIcon color="red" mb={6} onClick={() => onChange(rows.filter((r) => r.key !== row.key))} aria-label="Remove document">
                <IconTrash size={16} />
              </ActionIcon>
            </Tooltip>
          </Grid.Col>
        </Grid>
      ))}
      <Group gap="xs">
        <Button variant="light" size="xs" leftSection={<IconPlus size={14} />} onClick={() => onChange([...rows, emptyChecklistRow()])}>
          Add document
        </Button>
      </Group>
    </Stack>
  );
}

/** Rows with a name, in order; the server numbers new items rd-1, rd-2… in the same order. */
export function checklistPayload(rows: ChecklistRow[]) {
  return rows
    .filter((r) => r.name.trim())
    .map((r) => ({ key: r.savedKey, name: r.name.trim(), required: r.required, notes: r.notes.trim() || undefined }));
}

/** SC-NBD users from BDS. BD reviews the compiled dossier in BDS, so the reviewer is a BDS user. */
export function BdsReviewerSelect({ value, onChange, error, description, label = 'SC-NBD reviewer (BDS)' }: FieldProps & { description?: ReactNode; label?: string }) {
  const reviewers = useBdsReviewers();
  const options = (reviewers.data ?? [])
    .filter((u) => u.id)
    .map((u) => ({ value: u.id as string, label: u.teamName ? `${u.name} · ${u.teamName}` : u.name }));
  return (
    <Select
      label={label}
      description={reviewers.isError ? undefined : description}
      placeholder={reviewers.isLoading ? 'Loading from BDS…' : 'Choose an SC-NBD user'}
      data={options}
      value={value ?? null}
      onChange={(v) => onChange?.(v)}
      clearable
      searchable
      nothingFoundMessage="No SC-NBD users found in BDS"
      disabled={reviewers.isError}
      error={error ?? (reviewers.isError ? errorMessage(reviewers.error) : undefined)}
    />
  );
}
